import { isAxiosError, type AxiosRequestConfig } from 'axios';

import { useAuth } from '../hook_au/useAuth';
import apiClient from './apiClient';
import type { ImportWorkbookResult } from '../types/imports';

type RequestConfig<Response> = AxiosRequestConfig & { skipAuth?: boolean };

const UNKNOWN_ERROR = 'No pudimos procesar el archivo. Inténtalo de nuevo en unos segundos.';

const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (isAxiosError(error)) {
    const detail =
      typeof error.response?.data === 'object' &&
      error.response?.data &&
      'detail' in error.response.data &&
      typeof error.response.data.detail === 'string'
        ? error.response.data.detail
        : null;
    if (detail) return detail;
    if (error.response?.status === 401) return 'La sesión expiró. Vuelve a iniciar sesión para continuar.';
  }
  return fallback;
};

const buildWorkbookFormData = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return formData;
};

export function useImportsApi() {
  const { token } = useAuth();

  const request = async <Response>(config: RequestConfig<Response>): Promise<Response> => {
    if (!token && !config.skipAuth) {
      throw new Error('No hay sesión activa para llamar a la API de importación');
    }
    const response = await apiClient.request<Response>(config);
    return response.data;
  };

  const downloadTemplate = async () => {
    try {
      return await request<Blob>({
        method: 'GET',
        url: '/imports/template',
        responseType: 'blob',
      });
    } catch (error) {
      throw new Error(getApiErrorMessage(error, 'No pudimos descargar la plantilla Excel.'));
    }
  };

  const validateWorkbook = async (file: File) => {
    try {
      return await request<ImportWorkbookResult>({
        method: 'POST',
        url: '/imports/validate',
        data: buildWorkbookFormData(file),
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (error) {
      throw new Error(getApiErrorMessage(error, UNKNOWN_ERROR));
    }
  };

  const executeWorkbook = async (file: File) => {
    try {
      return await request<ImportWorkbookResult>({
        method: 'POST',
        url: '/imports/execute',
        data: buildWorkbookFormData(file),
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (error) {
      throw new Error(getApiErrorMessage(error, UNKNOWN_ERROR));
    }
  };

  return {
    downloadTemplate,
    validateWorkbook,
    executeWorkbook,
  };
}
